import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { formatPct } from "@/lib/gente-gestao/format";
import { csvRespostasUrl, downloadGgCsv, type GgResultadosDto } from "@/services/genteGestao";
import { TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

const GRUPO_LABEL: Record<string, string> = {
  PROMOTOR: "Promotor",
  NEUTRO: "Neutro",
  DETRATOR: "Detrator",
};

export function AbaResultados({ resultados }: { resultados: GgResultadosDto }) {
  const { cards, npsBreakdown, distribuicao0a10, dimensoes, porSetor, comentarios, adesaoPorSetor, adesaoPorDispositivo, evolucao, individuais, pesquisa } = resultados;
  const [filtroPergunta, setFiltroPergunta] = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState("");
  const [busca, setBusca] = useState("");

  const perguntasTexto = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of comentarios) map.set(c.perguntaId, c.perguntaTexto);
    return [...map.entries()].map(([value, label]) => ({ value, label: label.slice(0, 80) }));
  }, [comentarios]);

  const comentariosFiltrados = comentarios.filter((c) => {
    if (filtroPergunta && c.perguntaId !== filtroPergunta) return false;
    if (filtroGrupo && c.grupo !== filtroGrupo) return false;
    if (busca && !c.texto.toLowerCase().includes(busca.toLowerCase())) return false;
    return true;
  });

  const stacked = [
    {
      nome: "eNPS",
      detratores: Math.round(npsBreakdown.pctDetratores),
      neutros: Math.round(npsBreakdown.pctNeutros),
      promotores: Math.round(npsBreakdown.pctPromotores),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void downloadGgCsv(csvRespostasUrl(pesquisa.id), `respostas-${pesquisa.id}.csv`)}
        >
          Exportar CSV das respostas
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white shadow-card" style={{ borderLeftWidth: 4, borderLeftColor: cards.zona?.cor ?? "#94a3b8" }}>
          <Card
            title="eNPS"
            value={cards.enps == null ? "—" : String(cards.enps)}
            subtitle={cards.zona ? cards.zona.label : "Sem respostas"}
          />
        </div>
        <Card title="Respondentes" value={String(cards.respondentes)} />
        <Card title="Adesão" value={formatPct(cards.adesao, 0)} subtitle={`${cards.convidados} convidados`} />
        <Card
          title="NPS de produto"
          value={cards.npsProduto == null ? "—" : String(cards.npsProduto)}
          subtitle={cards.zonaProduto?.label}
        />
      </div>

      <ChartContainer title="Promotores, neutros e detratores" height={120} empty={npsBreakdown.total === 0}>
        <BarChart data={stacked} layout="vertical" margin={{ left: 8, right: 16 }}>
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" dataKey="nome" hide />
          <Tooltip cursor={TOOLTIP_CURSOR_BAND} />
          <Legend />
          <Bar dataKey="detratores" name="Detratores" stackId="a" fill="#b3261e" />
          <Bar dataKey="neutros" name="Neutros" stackId="a" fill="#94a3b8" />
          <Bar dataKey="promotores" name="Promotores" stackId="a" fill="#2e7d32" />
        </BarChart>
      </ChartContainer>

      <ChartContainer title="Distribuição das notas 0–10" height={240} empty={npsBreakdown.total === 0}>
        <BarChart data={distribuicao0a10}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="nota" />
          <YAxis allowDecimals={false} />
          <Tooltip cursor={TOOLTIP_CURSOR_BAND} />
          <Bar dataKey="count" name="Respostas">
            {distribuicao0a10.map((d) => (
              <Cell key={d.nota} fill={d.nota <= 6 ? "#b3261e" : d.nota <= 8 ? "#94a3b8" : "#2e7d32"} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Dimensões (pior → melhor)</h3>
        {dimensoes.length === 0 ? (
          <p className="text-sm text-slate-500">Sem escalas 1–5 nesta pesquisa.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2">Dimensão</th>
                <th>Média</th>
                <th>% favorável</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dimensoes.map((d) => (
                <tr key={d.dimensao} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{d.label}</td>
                  <td>{d.media ?? "—"}</td>
                  <td>{formatPct(d.favorabilidade, 0)}</td>
                  <td>
                    {d.atencao && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        Ponto de atenção
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Por setor</h3>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2">Setor</th>
              <th>Respondentes</th>
              <th>eNPS</th>
            </tr>
          </thead>
          <tbody>
            {porSetor.map((s) => (
              <tr key={s.setor} className="border-t border-slate-100">
                <td className="py-2">{s.setor}</td>
                <td>{s.oculto ? "—" : s.respondentes}</td>
                <td>{s.oculto ? s.motivo : s.enps ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
          <h3 className="mb-2 text-sm font-semibold">Adesão por setor</h3>
          <ul className="text-sm text-slate-700">
            {adesaoPorSetor.map((s) => (
              <li key={s.setor} className="flex justify-between border-t border-slate-100 py-1.5">
                <span>{s.setor}</span>
                <span>{s.oculto ? "Dados insuficientes para preservar a confidencialidade" : formatPct(s.adesao, 0)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
          <h3 className="mb-2 text-sm font-semibold">Por dispositivo</h3>
          <ul className="text-sm text-slate-700">
            {adesaoPorDispositivo.map((s) => (
              <li key={s.dispositivo} className="flex justify-between border-t border-slate-100 py-1.5">
                <span>{s.dispositivo}</span>
                <span>{s.oculto ? "Dados insuficientes para preservar a confidencialidade" : s.respostas}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <ChartContainer title="Evolução do eNPS" height={240} empty={evolucao.filter((e) => e.enps != null).length < 2}>
        <LineChart data={evolucao}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="titulo" />
          <YAxis domain={[-100, 100]} />
          <Tooltip cursor={TOOLTIP_CURSOR_BAND} />
          <Line type="monotone" dataKey="enps" name="eNPS" stroke="#B03858" strokeWidth={2} />
        </LineChart>
      </ChartContainer>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Comentários abertos</h3>
        <div className="mb-3 grid gap-2 md:grid-cols-3">
          <Select
            value={filtroPergunta}
            onChange={(e) => setFiltroPergunta(e.target.value)}
            options={perguntasTexto}
            placeholder="Todas as perguntas"
          />
          <Select
            value={filtroGrupo}
            onChange={(e) => setFiltroGrupo(e.target.value)}
            options={[
              { value: "PROMOTOR", label: "Promotores" },
              { value: "NEUTRO", label: "Neutros" },
              { value: "DETRATOR", label: "Detratores" },
            ]}
            placeholder="Todos os grupos"
          />
          <Input placeholder="Buscar palavra" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        {comentariosFiltrados.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum comentário.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {comentariosFiltrados.map((c, i) => (
              <li key={`${c.perguntaId}-${i}`} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                <p className="text-xs text-slate-500">
                  {c.perguntaTexto.slice(0, 80)}
                  {c.grupo ? ` · ${GRUPO_LABEL[c.grupo]}` : ""}
                  {c.colaboradorNome ? ` · ${c.colaboradorNome}` : ""}
                </p>
                <p className="mt-1 text-slate-800">{c.texto}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {individuais && (
        <div className="overflow-auto rounded-xl border border-slate-200 bg-white p-4 shadow-card">
          <h3 className="mb-3 text-sm font-semibold">Respostas individuais</h3>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2">Nome</th>
                <th>Usuário</th>
                <th>Setor</th>
                <th>Itens</th>
              </tr>
            </thead>
            <tbody>
              {individuais.map((row) => (
                <tr key={row.colaboradorId} className="border-t border-slate-100">
                  <td className="py-2">{row.nome}</td>
                  <td>{row.usuario}</td>
                  <td>{row.setor ?? "—"}</td>
                  <td>{row.itens.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
